import React, { useMemo, useState } from 'react';
import { GridCustomCellProps } from '@progress/kendo-react-grid';
import createGridCustomCells, { CustomToolTipCell } from '@components-clean/core/grid/custom-cell';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import { DateCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { ReviewPlanGridRowDto, ReviewPlanUpdateDto } from '@interfaces-clean/review-plan.interface';
import EditReviewPlanDialog from '@pages-clean/admin/study/manage-review-plans/dialogs/edit-review-plan-dialog';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useLocalState, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { ReviewPlanAPI } from '@apis/bff-api/study/review-plans';

const { TooltipCell } = createGridCustomCells();

interface ReviewPlanRow {
    id: number;
    recordId: number;
    active: boolean;
    type: string;
    description: string;
    dataListingsCount: number;
    dataListingNames: string[];
    watchedFieldsCount: number;
    createdBy: string;
    createdOn: string;
    updatedBy: string;
    updatedOn: string;
    editEnabled: boolean;
}

const mapToRow = (dto: ReviewPlanGridRowDto): ReviewPlanRow => ({
    id: dto.id ?? 0,
    recordId: dto.recordId ?? 0,
    active: dto.active ?? false,
    type: dto.reviewTypeName ?? '',
    description: dto.description ?? '',
    dataListingsCount: dto.dataListingsCount ?? 0,
    dataListingNames: dto.dataListingNames ?? [],
    watchedFieldsCount: dto.watchedFieldsCount ?? 0,
    createdBy: dto.createdBy ?? '',
    createdOn: dto.createdOn ?? '',
    updatedBy: dto.updatedBy ?? '',
    updatedOn: dto.updatedOn ?? '',
    editEnabled: dto.editEnabled ?? false,
});

const DataListingsCountCell = (props: GridCustomCellProps) => {
    const { dataItem, ...rest } = props;
    const row = dataItem as ReviewPlanRow;

    return (
        <CustomToolTipCell
            dataItem={{
                content: `${row.dataListingsCount}`,
                tooltipItems: row.dataListingNames.length ? row.dataListingNames : undefined,
            }}
            {...rest}
        />
    );
};

const ManageReviewPlans = () => {
    const { t } = useTranslation();
    const { state } = useLocalState();
    const studyId = (state?.selectedStudy?.id as string) ?? null;
    const user = useAuthorization();
    const canEdit =
        user?.canWrite?.({
            module: Constants.Modules.SM,
            element: Constants.DataElements.SubjectMonitoring,
            permission: Permissions.ReadWrite,
        }) ?? false;

    const { data: rawData, isValidating } = ReviewPlanAPI.useGetAll(studyId);

    const [editingId, setEditingId] = useState<number | null>(null);
    const { data: editingDetail, isValidating: isLoadingDetail } = ReviewPlanAPI.useGetById(studyId, editingId);
    const { trigger: triggerUpdate } = ReviewPlanAPI.useUpdate(studyId, editingId);

    const rows = useMemo<ReviewPlanRow[]>(() => (rawData ?? []).map(mapToRow), [rawData]);

    const rowMenuActions: RowMenuAction[] = [
        {
            id: 0,
            icon: ['far', 'pen-to-square'],
            label: t('admin:smReviewPlans.editDialog.title'),
            onClick: (dataItem) => {
                const row = dataItem as ReviewPlanRow;
                if (!canEdit || !row.editEnabled) return;
                setEditingId(row.recordId);
            },
            rowDisabled: (dataItem) => {
                const row = dataItem as ReviewPlanRow;
                return !canEdit || !row.editEnabled;
            },
        },
    ];

    const columns = useMemo<GridColumnProps[]>(
        () => [
            {
                id: 'active',
                title: t('admin:smReviewPlans.grid.columns.active'),
                field: 'active',
                filter: 'boolean',
                width: 100,
                cells: { data: YesNoCell },
            },
            {
                id: 'type',
                title: t('admin:smReviewPlans.grid.columns.reviewType'),
                field: 'type',
                filter: 'text',
                minWidth: 150,
                lovData: rows.map((r) => r.type).filter((v, i, a) => a.indexOf(v) === i),
            },
            {
                id: 'description',
                title: t('admin:smReviewPlans.grid.columns.description'),
                field: 'description',
                filter: 'text',
                minWidth: 200,
                cells: {
                    data: TooltipCell({
                        isCsv: false,
                    }),
                },
            },
            {
                id: 'dataListingsCount',
                title: t('admin:smReviewPlans.grid.columns.dataListingCount'),
                field: 'dataListingsCount',
                filter: 'numeric',
                width: 80,
                cells: { data: DataListingsCountCell },
            },
            {
                id: 'watchedFieldsCount',
                title: t('admin:smReviewPlans.grid.columns.watchColumnCount'),
                field: 'watchedFieldsCount',
                filter: 'numeric',
                width: 80,
            },
            {
                id: 'createdBy',
                title: t('admin:smReviewPlans.grid.columns.createdBy'),
                field: 'createdBy',
                filter: 'text',
                minWidth: 130,
            },
            {
                id: 'createdOn',
                title: t('admin:smReviewPlans.grid.columns.createdOn'),
                field: 'createdOn',
                filter: 'date',
                width: 1,
                minWidth: 100,
                cells: { data: DateCell },
                cellName: 'DateCell',
            },
            {
                id: 'updatedBy',
                title: t('admin:smReviewPlans.grid.columns.updatedBy'),
                field: 'updatedBy',
                filter: 'text',
                minWidth: 130,
            },
            {
                id: 'updatedOn',
                title: t('admin:smReviewPlans.grid.columns.updatedOn'),
                field: 'updatedOn',
                filter: 'date',
                width: 1,
                minWidth: 100,
                cells: { data: DateCell },
                cellName: 'DateCell',
            },
        ],
        [rows, t],
    );

    const handleUpdate = async (dto: ReviewPlanUpdateDto) => {
        try {
            await triggerUpdate(dto);
            ShowSuccess(t('common:actionsSuccessfulToast'));
            setEditingId(null);
        } catch {
            ShowError(t('common:errorMessage'));
        }
    };

    return (
        <div className="k-h-full">
            {editingId != null && !isLoadingDetail && editingDetail && (
                <EditReviewPlanDialog
                    reviewPlan={editingDetail}
                    onClose={() => setEditingId(null)}
                    onSubmit={handleUpdate}
                />
            )}
            {rows.length === 0 && !isValidating && <p className="k-m-4">{t('admin:smReviewPlans.grid.noRecords')}</p>}
            <Grid
                title={t('admin:smReviewPlans.grid.title')}
                testDataId="review-plans-grid"
                gridKey={!rows ? 0 : rows.length}
                data={rows}
                columns={columns}
                loading={isValidating}
                useLoadingOverlay
                showAddIcon={false}
                canEdit={canEdit}
                rowMenuActions={rowMenuActions}
                maxHeight="auto"
                exportFilename={`${state?.selectedStudy?.studyNumber}_Review Plans_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`}
                service={AuditServiceType.SUBJECT_MONITORING}
                area={AuditApplicationArea.SUBJECT_MONITORING}
                dataType={AuditDataType.EXCEL_EXPORT}
                dataItemKey="id"
                scrollable="virtual"
            />
        </div>
    );
};

export default ManageReviewPlans;
